// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiUserInputCamera.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FcCameraIdentification } from 'react-icons/fc';
import { FaPaperPlane } from 'react-icons/fa';

// IMPORTANT: re-use the same Firebase + config helpers as the text nutrition flow
import { db } from '../../../../../firebase/firebaseConfig';
import {
  loadApiKeysDoc,
  extractGeminiModelConfig
} from './AiMealFirebaseConfig';

const DEFAULT_MODEL_ID = 'gemini-2.5-flash-lite';

const GEMINI_HTTP_CONFIG = {
  headerName: 'x-goog-api-key',
  headerPrefix: ''
};


async function getGeminiApiConfig() {
  // Optional environment variable override
  if (typeof process !== 'undefined' && process.env?.REACT_APP_GEMINI_API_KEY) {
    const environmentKey = process.env.REACT_APP_GEMINI_API_KEY.trim();
    if (environmentKey) {
      return {
        apiKey: environmentKey,
        baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
        endpoint: `models/${DEFAULT_MODEL_ID}:generateContent`
      };
    }
  }

  if (!db) {
    console.warn('[AiUserInputCamera] Firestore db is not available');
    return {
      apiKey: null,
      baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
      endpoint: `models/${DEFAULT_MODEL_ID}:generateContent`
    };
  }

  try {
    const apiKeys = await loadApiKeysDoc(db, {
      collection: 'chattxtaiuse',
      document: 'textuseai',
      field: 'apiKeys'
    });

    if (!apiKeys) {
      console.warn(
        '[AiUserInputCamera] apiKeys document missing; falling back to defaults without key'
      );
      return {
        apiKey: null,
        baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
        endpoint: `models/${DEFAULT_MODEL_ID}:generateContent`
      };
    }

    const modelConfig = extractGeminiModelConfig(apiKeys, DEFAULT_MODEL_ID);

    const baseUrl =
      modelConfig.baseUrl || 'https://generativelanguage.googleapis.com/v1beta';

    const endpoint =
      modelConfig.rawEndpoint ||
      `${modelConfig.modelPath || `models/${DEFAULT_MODEL_ID}`}:generateContent`;

    return {
      apiKey: modelConfig.apiKey || null,
      baseUrl,
      endpoint
    };
  } catch (error) {
    console.warn(
      '[AiUserInputCamera] Failed to load Gemini config from Firestore (falling back to defaults):',
      error
    );
    return {
      apiKey: null,
      baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
      endpoint: `models/${DEFAULT_MODEL_ID}:generateContent`
    };
  }
}


function createCameraEventMetadata() {
  const timestampIso = new Date().toISOString();
  const idempotencyKey = `camera_${timestampIso}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
  const requestId = idempotencyKey;
  const traceId = idempotencyKey;

  return {
    source: 'AiUserInputCamera',
    requestId,
    traceId,
    visited: ['AiUserInputCamera'],
    hopCount: 1,
    publishedAt: timestampIso,
    idempotencyKey,
    changeHash: idempotencyKey
  };
}

/**
 * Extract plain text from a Gemini generateContent payload.
 */
function extractTextFromGeminiPayload(payload) {
  if (
    !payload ||
    !payload.candidates ||
    !Array.isArray(payload.candidates) ||
    payload.candidates.length === 0
  ) {
    return '';
  }

  const firstCandidate = payload.candidates[0];
  if (!firstCandidate || !firstCandidate.content) {
    return '';
  }

  const parts = firstCandidate.content.parts || [];
  const textPieces = [];

  for (const part of parts) {
    if (typeof part.text === 'string') {
      textPieces.push(part.text);
    }
  }

  return textPieces.join(' ').trim();
}

/**
 * Utility to convert a File/Blob to base64 string (without data: prefix).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('file_read_failed'));
        return;
      }
      const base64Index = result.indexOf('base64,');
      if (base64Index === -1) {
        resolve(result);
        return;
      }
      resolve(result.slice(base64Index + 'base64,'.length));
    };
    reader.onerror = () => {
      reject(new Error('file_read_failed'));
    };
    reader.readAsDataURL(file);
  });
}


async function callGeminiImageToText(selectedImageFile, eventMeta) {
  const { apiKey, baseUrl, endpoint } = await getGeminiApiConfig();

  if (!apiKey) {
    return {
      ok: false,
      error: 'gemini_api_key_missing',
      text: '',
      rawResponse: null
    };
  }

  if (!selectedImageFile) {
    return {
      ok: false,
      error: 'no_file',
      text: '',
      rawResponse: null
    };
  }

  const base64Data = await fileToBase64(selectedImageFile);

  const url = `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;

  const body = {
    contents: [
      {
        role: 'user',
        parts: [
          {
            text: [
              'You are helping a user log what they ate in a nutrition tracking app.',
              '',
              'First, decide if the image is:',
              '  a nutrition facts label, or',
              '  a meal/food photo.',
              '',
              'If it is a nutrition facts label, respond in EXACTLY ONE LINE:',
              '  LABEL: <product name>; serving size <value>; calories <value>; calories from fat <value>; total fat <value>; saturated fat <value>; trans fat <value>; cholesterol <value>; sodium <value>; total carbohydrate <value>; dietary fiber <value>; total sugars <value>; protein <value>; vitamin D <value>; calcium <value>; iron <value>; potassium <value>.',
              'Read all numbers and units from the label. Double check iron, If missing, use 0 with units (e.g., "vitamin D 0mcg").',
              '',
              'If it is a meal/food photo, describe it as a single natural sentence:',
              '  "I had an estimated amount 1 cup broccoli, 1/2 cup carrots, 4 oz salmon, 1/2 cup brown rice, and 1/4 cup almonds."',
              '',
              '- Use conversational language like telling a friend what you ate',
              '- List all visible items with portions in one flowing sentence',
              '- Include approximate portions (cups, tablespoons, oz, slices, pieces)',
              '- Separate items with commas, use "and" before the last item',
              '- NO category headings, NO bullet points, NO structured format',
              '- Just one descriptive sentence describing everything you see including the protions'
            ].join(' ')
          },
          {
            inlineData: {
              mimeType: selectedImageFile.type || 'image/jpeg',
              data: base64Data
            }
          }
        ]
      }
    ],
    generationConfig: {
      temperature: 0.1,
      topP: 0.1,
      topK: 32,
      maxOutputTokens: 512
    },
    safetySettings: [],
    systemInstruction: {
      role: 'system',
      parts: [
        {
          text: JSON.stringify({
            cameraEventMeta: eventMeta
          })
        }
      ]
    }
  };

  const headers = {
    'content-type': 'application/json',
    [GEMINI_HTTP_CONFIG.headerName]: `${
      GEMINI_HTTP_CONFIG.headerPrefix || ''
    }${apiKey}`
  };

  const httpResponse = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (!httpResponse || !httpResponse.ok) {
    return {
      ok: false,
      error: `http_${httpResponse ? httpResponse.status : 'error'}`,
      text: '',
      rawResponse: null
    };
  }

  const payload = await httpResponse.json();
  const text = extractTextFromGeminiPayload(payload);

  return {
    ok: true,
    error: null,
    text,
    rawResponse: payload
  };
}


export default function AiUserInputCamera({
  ariaLabel,
  title,
  onImageChange,
  onImageTextReady,
  onStatusChange,
  canInlineSend = true,
  sendAriaLabel,
  sendTitle
}) {
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);

  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  /**
   * Notify parent that the selected image changed.
   * The parent uses this to set hasAttachedImage and drive textarea / placeholder state.
   */
  const notifyParentImageChange = useCallback(
    (file, previewUrl) => {
      if (typeof onImageChange === 'function') {
        onImageChange({ file, previewUrl });
      }
    },
    [onImageChange]
  );

  /**
   * Notify parent that AI image→text processing finished.
   */
  const notifyParentImageTextReady = useCallback(
    (payload) => {
      if (typeof onImageTextReady === 'function') {
        onImageTextReady(payload);
      }
    },
    [onImageTextReady]
  );

  /**
   * Clear the currently selected image:
   * - Reset local file and preview state.
   * - Revoke any object URL.
   * - Reset the file input so the same file can be chosen again.
   * - Notify the parent that there is no attached image.
   */
  const clearSelectedImage = useCallback(() => {
    setSelectedImageFile(null);

    setSelectedImagePreviewUrl((previousUrl) => {
      if (previousUrl) {
        try {
          URL.revokeObjectURL(previousUrl);
        } catch {
          // ignore
        }
      }
      return null;
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    notifyParentImageChange(null, null);
  }, [notifyParentImageChange]);

  /**
   * Camera button click:
   * - If an image is already selected, treat this as a reset.
   * - If no image is selected, open the file picker as usual.
   */
  const handleCameraButtonClick = () => {
    if (selectedImagePreviewUrl || selectedImageFile) {
      clearSelectedImage();
      return;
    }

    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  /**
   * File input change handler:
   * - Create a preview URL for the first selected file.
   * - Store file and preview in state.
   * - Notify parent that an image is now attached.
   */
  const handleFileChange = (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }

    const file = fileList[0];
    const objectUrl = URL.createObjectURL(file);

    setSelectedImageFile(file);
    setSelectedImagePreviewUrl((previousUrl) => {
      if (previousUrl) {
        try {
          URL.revokeObjectURL(previousUrl);
        } catch {
          // ignore
        }
      }
      return objectUrl;
    });

    notifyParentImageChange(file, objectUrl);
  };

  /**
   * Explicit "X" button on the image preview.
   */
  const handleRemoveImageClick = () => {
    if (isSending) return;
    clearSelectedImage();
  };

  /**
   * Inline send: call Gemini with the selected image.
   * When done:
   *   - Clear image.
   *   - Notify parent with AI text so it can be parsed or inserted into the textarea.
   */
  const handleInlineSendClick = useCallback(async () => {
    if (!selectedImageFile || isSending || !canInlineSend) return;

    setIsSending(true);
    const eventMeta = createCameraEventMetadata();

    // Emit 'processing' status to parent
    if (typeof onStatusChangeRef.current === 'function') {
      onStatusChangeRef.current({ status: 'processing' });
    }

    try {
      const result = await callGeminiImageToText(selectedImageFile, eventMeta);

      notifyParentImageTextReady({
        text: result.text || '',
        error: result.error,
        rawResponse: result.rawResponse,
        eventMeta
      });

      // NOTE: Do NOT set status to 'idle' here - parent will do it when text appears in input
    } catch (error) {
      console.warn('[AiUserInputCamera] Unexpected camera AI error:', error);
      notifyParentImageTextReady({
        text: '',
        error: 'unexpected_error',
        rawResponse: null,
        eventMeta
      });

      // Reset to idle on error
      if (typeof onStatusChangeRef.current === 'function') {
        onStatusChangeRef.current({ status: 'idle' });
      }
    } finally {
      clearSelectedImage();
      setIsSending(false);
    }
  }, [
    selectedImageFile,
    isSending,
    canInlineSend,
    notifyParentImageTextReady,
    clearSelectedImage
  ]);

  /**
   * Cleanup any remaining object URL on unmount.
   */
  useEffect(() => {
    return () => {
      if (selectedImagePreviewUrl) {
        try {
          URL.revokeObjectURL(selectedImagePreviewUrl);
        } catch {
          // ignore
        }
      }
    };
  }, [selectedImagePreviewUrl]);

  const isInlineSendDisabled =
    !selectedImageFile || !canInlineSend || isSending;

  return (
    <div className="aiu-camera-inline">
      {/* Inline preview inside the input pill, with X to remove */}
      {selectedImagePreviewUrl && (
        <div className="aiu-camera-preview aiu-camera-preview-inline">
          <div className="aiu-camera-preview-inner">
            <img
              src={selectedImagePreviewUrl}
              alt="Preview of selected meal or product"
              className="aiu-camera-preview-img"
            />
            <button
              type="button"
              className="aiu-camera-remove-btn"
              onClick={handleRemoveImageClick}
              aria-label="Remove selected image"
              title="Remove selected image"
              disabled={isSending}
            >
              ×
            </button>
          </div>

          <button
            type="button"
            className="aiu-camera-send-btn"
            onClick={handleInlineSendClick}
            disabled={isInlineSendDisabled}
            aria-label={sendAriaLabel || 'Send'}
            title={sendTitle || 'Send'}
          >
            <FaPaperPlane
              className="aiu-send-icon"
              aria-hidden
            />
          </button>
        </div>
      )}

      {/* Camera / gallery button (keeps original CSS classes) */}
      <button
        type="button"
        className="btn p-0 aiu-icon-btn-ghost"
        aria-label={ariaLabel}
        title={title}
        onClick={handleCameraButtonClick}
        disabled={isSending}
      >
        <FcCameraIdentification
          className="aiu-icon aiu-camera-icon"
          aria-hidden
        />
      </button>

      {/* Hidden file input: works on both phone and desktop for camera or gallery/file upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
}
